import React, { useMemo, useRef, useEffect } from 'react';
import { useUpdate, useNotify, useDataProvider } from 'react-admin';
import type { ColDef } from 'ag-grid-community';
import AgGrid from '../../components/ag-grid/ag-grid';
import { autoLoad } from '../../utils/common';
import RenderControls from '../../components/ra-list/RenderControls';
import { dayjs, formatLocalTime } from '../../components/ex_dayjs';
import NumericEditor from '../../components/controls/numeric-editor';
import { Box, Button, Dialog, DialogTitle, DialogContent, IconButton } from '@mui/material';
import type { ICellRendererParams } from 'ag-grid-community';
import CloseIcon from '@mui/icons-material/Close';
import { ListingStatus } from './types';
import type { AdditionalListing, FilterControl, MasterViewRow } from './types';

function ActionColumnNoDelete (params: ICellRendererParams): JSX.Element {
    const editingCells = params.api.getEditingCells();
    const isCurrentRowEditing = editingCells.some((cell) => cell.rowIndex === params.node.rowIndex);

    if (isCurrentRowEditing) {
        return (
            <Box component="form" sx={{ '& > :not(style)': { m: 1 } }}>
                <button type='button' data-action="update"> Update  </button>
                <button type='button' data-action="cancel" > Cancel </button>
            </Box>
        );
    } else {
        return (
            <Box component="form" sx={{ '& > :not(style)': { m: 1 } }}>
                <button type='button' data-action="edit" > Edit  </button>
            </Box>
        );
    }
}

const LISTING_STATUS: Record<number, { label: string; color: string }> = {
    [ListingStatus.NEW]: { label: 'New', color: 'blue' },
    [ListingStatus.ACTIVE]: { label: 'Active', color: 'green' },
    [ListingStatus.EXCLUDED]: { label: 'Excluded', color: 'orange' },
    [ListingStatus.UNAVAILABLE]: { label: 'Unavailable', color: 'red' },
    [ListingStatus.PAUSED]: { label: 'Paused', color: 'grey' },
};

interface DetailsDialogProps {
    event: MasterViewRow;
    open: boolean;
    onClose: () => void;
}

const DetailsDialog: React.FC<DetailsDialogProps> = ({ event, open, onClose }) => {
    const refInput = useRef<any>(null);
    const refbtnFilter = useRef<HTMLElement>(null);
    const notify = useNotify();
    const dataProvider = useDataProvider();
    const [handleUpdate] = useUpdate();

    // Trigger initial load when dialog opens; delay allows AgGrid to initialize before search
    useEffect(() => {
        if (open) {
            setTimeout(() => {
                if (refInput.current) {
                    refInput.current.customFilterSearch({ eventId_eq: event.eventId });
                }
            }, 150);
        }
    }, [open, event.eventId]);

    const handleBulkSetStatus = (status: number): void => {
        const selectedRows = refInput.current?.getAgGridInstance()?.api?.getSelectedRows();
        if (!selectedRows || selectedRows.length === 0) {
            notify('No rows selected', { type: 'warning', autoHideDuration: 2000 });
            return;
        }
        const ids = selectedRows.map((row: AdditionalListing) => row.id);
        const statusLabel = LISTING_STATUS[status]?.label ?? String(status);
        dataProvider.updateMany('AdditionalListings', { ids, data: { status } })
            .then(() => {
                notify(`${ids.length} listing(s) set to ${statusLabel}`, { type: 'success', autoHideDuration: 2000 });
                autoLoad(refbtnFilter);
            })
            .catch((error: any) => {
                notify(`Update error: ${error.message}`, { type: 'error', autoHideDuration: 5000 });
            });
    };

    const defaultColDef = useMemo<ColDef>(() => ({
        sortable: true,
        resizable: true,
        flex: 1,
        minWidth: 80,
        filter: false,
        suppressMovable: true,
    }), []);

    const onCellClicked = (params: any): void => {
        if (params.column.colId !== 'action' || !params.event.target.dataset.action) return;
        const action = params.event.target.dataset.action;

        if (action === 'edit') {
            params.api.startEditingCell({
                rowIndex: params.node.rowIndex,
                colKey: params.columnApi.getDisplayedCenterColumns()[0].colId
            });
        }
        if (action === 'update') {
            params.api.stopEditing(false);
            handleUpdate('AdditionalListings', { id: params.data.id, data: params.data }, {
                onSuccess: () => {
                    notify('Update successfully', { type: 'success', autoHideDuration: 2000 });
                },
                onError: (error: any) => {
                    notify(`Update error: ${error.message}`, { type: 'error', autoHideDuration: 5000 });
                },
            });
            params.api.stopEditing();
        }
        if (action === 'cancel') {
            params.api.stopEditing(true);
        }
    };

    const onCellEditingStopped = (params: any): void => {
        params.api.refreshCells({ columns: ['action'], rowNodes: [params.node], force: true });
    };

    const onCellEditingStarted = (params: any): void => {
        params.api.refreshCells({ columns: ['action'], rowNodes: [params.node], force: true });
    };

    const colDefs: ColDef[] = [
        { headerCheckboxSelection: true, checkboxSelection: true, maxWidth: 50, sortable: false, resizable: false, flex: 0 },
        { field: 'id', headerName: 'ID', maxWidth: 130 },
        { field: 'section', headerName: 'Section', editable: true },
        { field: 'row', headerName: 'Row', editable: true, maxWidth: 90 },
        { field: 'seats', headerName: 'Seats', editable: true },
        {
            field: 'price', headerName: 'Price', editable: true, maxWidth: 110,
            cellEditor: NumericEditor,
            valueParser: (params: any) => Number(params.newValue),
            valueFormatter: (params: any) => params.value !== null && params.value !== undefined ? `$${Number(params.value).toFixed(2)}` : ''
        },
        { field: 'priceLevel', headerName: 'Price Level', editable: true },
        {
            field: 'numTix', headerName: 'NumTix', editable: true, maxWidth: 90,
            cellEditor: NumericEditor,
            valueParser: (params: any) => Number(params.newValue)
        },
        { field: 'type', headerName: 'Type', maxWidth: 100 },
        { field: 'exact', headerName: 'Exact', maxWidth: 70 },
        {
            field: 'status', headerName: 'Status', maxWidth: 110,
            valueFormatter: (params: any) => LISTING_STATUS[params.value]?.label ?? String(params.value),
            cellStyle: (params: any) => ({ color: LISTING_STATUS[params.value]?.color ?? 'inherit' })
        },
        {
            field: 'insertDate', headerName: 'Created', maxWidth: 160,
            valueFormatter: (params: any) => params.value ? dayjs.utc(params.value).fromNow() : ''
        },
        {
            field: 'modifiedDate', headerName: 'Modified', maxWidth: 160,
            valueFormatter: (params: any) => params.value ? dayjs.utc(params.value).fromNow() : ''
        },
        { field: 'modifiedBy', headerName: 'Modified By', maxWidth: 160 },
        {
            headerName: 'Action', minWidth: 150,
            cellRenderer: ActionColumnNoDelete,
            colId: 'action'
        }
    ];

    const controls: FilterControl[] = [
        { component: 'text', name: 'section', label: 'Section', xs: 1 },
        { component: 'text', name: 'row', label: 'Row', xs: 0.7 },
        { component: 'select', name: 'status_eq', label: 'Status', xs: 1, choices: [{ id: ListingStatus.NEW, name: 'New' }, { id: ListingStatus.ACTIVE, name: 'Active' }, { id: ListingStatus.EXCLUDED, name: 'Excluded' }, { id: ListingStatus.UNAVAILABLE, name: 'Unavailable' }] },
    ];

    const handleSearch = (filterList: Record<string, unknown>): void => {
        refInput.current.customFilterSearch({ ...filterList, eventId_eq: event.eventId });
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>
                    {event.eventUrl
                        ? <a href={event.eventUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', textDecoration: 'underline', cursor: 'pointer' }}>{event.eventName ?? `Event ${event.eventId}`}</a>
                        : (event.eventName ?? `Event ${event.eventId}`)
                    }
                    {event.eventSiteId ? ` — Site ${event.eventSiteId}` : ''}
                    {` — ${event.listingCount} listing(s)`}
                </span>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 1 }}>
                <RenderControls
                    controls={controls}
                    onSearch={handleSearch}
                    refFilter={refbtnFilter}
                    filterInline={true}
                    agSource='AdditionalListings'
                    agSort={{ field: 'modifiedDate', order: 'desc' }}
                    actions={
                        <>
                            <Button variant="contained" color="success" size="small" onClick={() => handleBulkSetStatus(ListingStatus.ACTIVE)}>
                                Set Active
                            </Button>
                            <Button variant="contained" color="warning" size="small" onClick={() => handleBulkSetStatus(ListingStatus.EXCLUDED)}>
                                Set Excluded
                            </Button>
                            <Button variant="contained" size="small" sx={{ bgcolor: 'grey.500', '&:hover': { bgcolor: 'grey.700' } }} onClick={() => handleBulkSetStatus(ListingStatus.PAUSED)}>
                                Set Paused
                            </Button>
                        </>
                    }
                />
                <AgGrid
                    ref={refInput}
                    columnDefs={colDefs}
                    defaultColDef={defaultColDef}
                    rowHeight={45}
                    rowSelection='multiple'
                    suppressRowClickSelection={true}
                    editType={'fullRow'}
                    onCellClicked={onCellClicked}
                    onCellEditingStopped={onCellEditingStopped}
                    onCellEditingStarted={onCellEditingStarted}
                    list={'AdditionalListings'}
                    defaultValues={{ sortField: { field: 'modifiedDate', order: 'desc' } }}
                    gridHeight='65vh'
                />
            </DialogContent>
        </Dialog>
    );
};

export default DetailsDialog;
